import { get, isNil, some, find } from 'lodash';
import { compose, propOr, curriedMap, prop, curriedFilter } from '@core/lib/fp';
import { UserRole } from '@core/api/types';

/**
 * Returns a new empty user with inherited properties from the user
 * provided as param
 *
 * @param {User?} baseUser
 * @returns {User}
 */
export function newUser(baseUser) {
  const language = get(baseUser, 'language', 'en');

  return {
    role: 'reader',
    language
  };
}

/**
 * @param {User} user
 * @returns {Boolean}
 */
export function isNewUser(user) {
  return isNil(user.created_at);
}

/**
 * Gets a list of the disabled features for a user
 *
 * @param {User} user
 * @returns {[String]}
 */
export const getDisabledFeatures = compose(
  curriedMap(prop('name')),
  curriedFilter(feature => feature.role === 'none'),
  propOr([], 'local_roles.features')
);

/**
 * Checks if feature is available for a particular account
 *
 * @param {Account} account
 * @param {Object} packages
 * @param {Object} feature
 * @returns {Boolean}
 */
export function featureIsAvailable(
  account,
  packages,
  { key, required_package }
) {
  return required_package
    ? !!packages[required_package]
    : get(account, `features.${key}.enabled`, false);
}

/**
 * Checks if feature is enabled for a particular user
 *
 * @param {User} user
 * @param {String} featureKey
 * @returns {Boolean}
 */
export function featureIsEnabled(user, featureKey) {
  const disabledFeatures = getDisabledFeatures(user);
  return !disabledFeatures.includes(featureKey);
}

/**
 * Checks if a user has the necessary role level
 *
 * @param {User} user
 * @param {String} role
 * @returns {Boolean}
 */
export function hasRoleLevel(user, role) {
  const roles = {
    custom: 0,
    reader: 1,
    editor: 2,
    admin: 3
  };
  const userRole = get(roles, user.role, -1);
  const targetRole = get(roles, role, -1);

  if (userRole === -1 || targetRole === -1) {
    return false;
  }

  return user.super_admin || userRole >= targetRole;
}

/**
 * Get user role
 *
 * @param {User} user
 * @returns {UserRole}
 */
export function getRole(user) {
  if (user.role === UserRole.Admin && user.super_admin) {
    return UserRole.SuperAdmin;
  }

  if (user.role === UserRole.Admin && !user.super_admin) {
    return UserRole.Admin;
  }

  return user.role;
}

/**
 * Checks if a custom user has no roles
 *
 * @param {User} user
 * @returns {Boolean}
 */
export function hasNoRoles(user) {
  return user.role === 'custom' && !user.has_local_roles;
}

/**
 * Checks if a custom user has a particular role on any app
 *
 * @param {User} user
 * @param {String} role
 * @returns {Boolean}
 */
export function hasCustomRole(user, role) {
  const appLocalRoles = get(user, 'local_roles.mobile_apps', []);
  return some(appLocalRoles, app => app.role === role);
}

/**
 * Checks if a custom user has a particular custom feature on any app, or
 * in a particular app if a token is provided
 *
 * @param {User} user
 * @param {String} customPermission
 * @param {String?} token
 * @returns {Boolean}
 */
export function hasCustomPermission(user, customPermission, token = null) {
  const appLocalRoles = get(user, 'local_roles.mobile_apps', []);

  if (token) {
    const app = find(appLocalRoles, { token });
    return get(app, customPermission, false);
  }

  return some(appLocalRoles, localRole =>
    get(localRole, customPermission, false));
}
